import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import type { XYCoord } from 'react-dnd'
import { GripVertical, Image, LayoutGrid, GalleryHorizontal, Tag, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react'
import { Badge } from '../ui/badge'
import type { StoreHomeComponent, StoreHomeComponentType } from '@/app/services/store-design.service'

const details: Record<StoreHomeComponentType, { label: string; icon: typeof Image }> = {
  HERO: { label: 'Banner Hero', icon: Image },
  SINGLE_BANNER: { label: 'Banner Individual', icon: Image },
  BANNER_GRID_3: { label: 'Banner 3 Espacios', icon: LayoutGrid },
  PRODUCT_CAROUSEL: { label: 'Carrusel de productos', icon: GalleryHorizontal },
  PROMOTION_SECTION_CARDS: { label: 'Sección de promociones', icon: LayoutGrid },
  PURCHASE_BENEFITS: { label: 'Beneficios por compra', icon: LayoutGrid },
}

export function SortableStoreSection({ component, title, index, moveSection, onToggle, onRemove, canEdit, canDelete, }: {
  component: StoreHomeComponent
  title: string
  index: number
  moveSection: (from: number, to: number) => void
  onToggle: () => void
  onRemove: () => void
  canEdit: boolean
  canDelete: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [{ handlerId }, drop] = useDrop({
    accept: 'store-section',
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover(item: { index: number }, monitor) {
      if (!ref.current || item.index === index) return
      const rect = ref.current.getBoundingClientRect()
      const offset = monitor.getClientOffset() as XYCoord
      if ((item.index < index && offset.y < rect.top + rect.height / 2) || (item.index > index && offset.y > rect.top + rect.height / 2)) return
      moveSection(item.index, index)
      item.index = index
    },
  })
  const [{ isDragging }, drag] = useDrag({
    type: 'store-section',
    item: { index },
    canDrag: canEdit,
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  })
  drag(drop(ref))
  const detail = details[component.component_type]
  const Icon = detail.icon

  return <div ref={ref} data-handler-id={handlerId} className={`flex items-center gap-3 rounded-lg border bg-card p-4 shadow-sm transition ${isDragging ? 'opacity-40' : ''}`}>
    {canEdit && (
      <GripVertical className="h-5 w-5 cursor-grab text-muted-foreground" />)}
    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
    <div className="min-w-0 flex-1"><p className="font-medium text-card-foreground">{title}</p><p className="text-sm text-muted-foreground">{detail.label}</p></div>
    <Badge variant="outline" className={component.is_active ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}>{component.is_active ? 'Visible' : 'Oculta'}</Badge>
    {canEdit && (
      <button
        type="button"
        onClick={onToggle}
        title={component.is_active ? 'Ocultar sección' : 'Mostrar sección'}
        className="rounded p-1.5 text-muted-foreground hover:bg-muted"
      >
        {component.is_active
          ? <ToggleRight className="h-5 w-5 text-primary" />
          : <ToggleLeft className="h-5 w-5" />}
      </button>
    )}

    {canDelete && (
      <button
        type="button"
        onClick={onRemove}
        title="Quitar sección"
        className="rounded p-1.5 text-red-600 hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    )}</div>
}
